/**
 * (owner, Sep 28: "we need some variance to them … A: that's a huge improvement, add them") What a townsperson looks like from
 * above: a kind from content/walkers (by weight), then their clothes, hair, hat, glasses, beard and build from the cosmetics
 * catalogue the players shop from — every pick hashed from the person's id, so a walker looks the same on every screen and from
 * frame to frame. The uniforms stay: police, guards, soldiers and burglars keep their colours and only their skin varies; a gang
 * keeps its jacket, a fare its taxi-yellow top, a bounty its red — they must be told apart at a glance.
 */
import type { Registry } from '../content/registry';
import type { Cosmetic, WalkerKind } from '../content/types';
import { hash } from '../world/rng';
import type { Person } from './entities';
import { catalogue, resolveLook, type Look, type ResolvedLook } from './cosmetics';

export interface NpcLook {
  look: ResolvedLook;
  /** the kind (content/walkers), for the uniformed roles none */
  kind?: WalkerKind;
  /** what they carry or wear over the top, and its colour */
  prop?: { kind: NonNullable<WalkerKind['prop']>; color: string };
  /** the trousers (the legs that step under the body) */
  legs: string;
  /** drawn this size (a kid smaller) */
  scale: number;
  gait: NonNullable<WalkerKind['gait']>;
}

/** the roles that keep their uniform (skin varies, nothing else) */
const UNIFORM = new Set(['police', 'guard', 'soldier', 'burglar']);
/** the roles whose top says who they are */
const ROLE_TOP: Partial<Record<NonNullable<Person['role']>, string>> = { fare: '#c9a040', bounty: '#8a1e1e', mechanic: '#b85a2a' };

/** trousers when the kind names none: jeans, blacks, greys, browns, khaki */
export const LEGS = ['#2c2e38', '#3a4a6a', '#34507a', '#1c1c24', '#5a5a60', '#4a3a2a', '#8a7a5a', '#2a3a2a'];
const u01 = (id: number, salt: number): number => hash(0x5eed, id, salt) / 4294967296;
function pick<T>(xs: readonly T[], id: number, salt: number): T | undefined { return xs.length ? xs[Math.floor(u01(id, salt) * xs.length)] : undefined; }

/** the kind of walker a person is, by weight */
export function walkerKindOf(reg: Registry, id: number): WalkerKind | undefined {
  const all = reg.all('walker').filter((w) => w.weight > 0); if (!all.length) return undefined;
  let t = u01(id, 1) * all.reduce((a, w) => a + w.weight, 0);
  for (const w of all) { t -= w.weight; if (t < 0) return w; }
  return all[all.length - 1];
}

const everyday = (c: Cosmetic): boolean => c.price < 1000 && !c.tags.includes('mask'); // (no crowns and no balaclavas on the way to the shops)
function idsOf(reg: Registry, slot: Cosmetic['slot'], only: string[] | undefined): string[] {
  return only?.length ? only.filter((id) => reg.get('cosmetic', id)) : catalogue(reg, slot).filter(everyday).map((c) => c.id);
}

/** the look of a non-player person (animals have none) */
export function npcLook(reg: Registry, p: Pick<Person, 'id' | 'role' | 'gang'>): NpcLook {
  const id = p.id, l: Look = {};
  l.skin = pick(catalogue(reg, 'skin').map((c) => c.id), id, 2);
  if (p.role && UNIFORM.has(p.role)) return { look: { ...resolveLook(reg, l), hair: undefined }, /* (the cap, the helmet, the balaclava cover it) */ legs: p.role === 'soldier' ? '#4a5530' : p.role === 'burglar' ? '#1c1c20' : '#1e2a44', scale: 1, gait: 'walk' };
  const kind = p.role === 'keeper' ? reg.get('walker', 'townsfolk') : walkerKindOf(reg, id);
  const withoutNone = (slot: Cosmetic['slot'], only: string[] | undefined) => idsOf(reg, slot, only).filter((x) => reg.get('cosmetic', x)?.style !== 'none');
  l.top = pick(idsOf(reg, 'top', kind?.tops), id, 3);
  l.hair = pick(idsOf(reg, 'hair', kind?.hair).filter((x) => reg.get('cosmetic', x)?.style !== 'none'), id, 4);
  if (u01(id, 5) < (kind?.hatChance ?? 0.2)) l.hat = pick(withoutNone('hat', kind?.hats).filter((x) => kind?.hats || !reg.get('cosmetic', x)?.tags.includes('uniform')), id, 6);
  if (u01(id, 7) < (kind?.glassesChance ?? 0.12)) l.glasses = pick(withoutNone('glasses', undefined), id, 8);
  if (u01(id, 9) < (kind?.beardChance ?? 0.15)) l.beard = pick(withoutNone('beard', undefined), id, 10);
  l.body = pick(idsOf(reg, 'body', kind?.bodies), id, 11);
  const look = resolveLook(reg, l);
  const roleTop = p.role === 'gang' ? p.gang?.color : p.role ? ROLE_TOP[p.role] : undefined; if (roleTop) look.top = roleTop;
  const propKind = p.role === 'keeper' ? 'apron' : kind?.prop ?? 'none';
  const prop = propKind !== 'none' ? { kind: propKind, color: pick(kind?.propColors ?? ['#e8e2d0'], id, 12) ?? '#e8e2d0' } : undefined;
  return { look, kind, prop, legs: pick(kind?.legColors ?? LEGS, id, 13) ?? LEGS[0], scale: kind?.scale ?? 1, gait: kind?.gait ?? 'walk' };
}
